// The hotbar: what you could make, down the left edge (PLAN-UI §28.3, U4;
// screen-mock `#hot`).
//
// **Persistent chrome, because the offer is a fact about the board.** Forging
// used to be a machine's menu — find a foundry, select it, read its recipes —
// and since U-R the bill is split between every foundry touching the landing
// square, so no single machine's list is the truth any more. `hotbar.ts` does
// that arithmetic; this draws it and nothing else.
//
// Explicit props, no store reads: the model is memoised by the shell, and a
// component that fetched its own state would recompute the whole union on
// every render of everything above it.

import { type ReactNode, useRef, useState } from "react";

import type {
  CarryPoint,
  HotbarView,
  OptionId,
  PriceRow,
  StructureName
} from "src/api";
import { BUILDING_KIND, DIR_INDEX, DIR_NAME, PIECE_TYPE, STRUCTURE_KIND } from "src/client/ai/render/factNames";
import type { Direction } from "src/shared/board";
import { FacingDial } from "./FacingDial";
import { PieceMark } from "./PieceMark";
import { Price } from "./unitRows";
import { useTileCarry } from "./useTileCarry";
import {
  HoverCard,
  Icon,
  type IconName,
  Popover,
  SectionTitle,
  styled,
  Tile,
  TileCaption,
  TileCount,
  TileGlyph,
  useChromeRect
} from "./kit";

export interface HotbarProps {
  view: HotbarView;
  /** A tile clicked: `session.choose`. */
  onChoose(option: OptionId): void;
  /**
   * A tile dragged off the bar rather than clicked: `session.lift` — armed
   * and put in the hand, to be let go of on a lit square. The same act as the
   * click and the click after it.
   */
  onLift(option: OptionId, at: CarryPoint): void;
  /** Where each board direction points on screen, measured. `facingAngles`. */
  facingAngles: Record<Direction, number> | null;
}

// Static: nothing here moves per frame, so the whole column is one emotion
// class rather than a `style` object rewritten on every render.
const Column = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 14,
  top: "50%",
  // Not `-50%`: the column reads as centred when its optical mass sits a
  // little high, which is the mock's own number.
  transform: "translateY(-52%)",
  zIndex: theme.kit.z.chrome,
  display: "flex",
  flexDirection: "column",
  gap: 7
}));

/** The heading over a group. Centred over tiles of a fixed width. */
const GroupTitle = styled(SectionTitle)({ textAlign: "center" });

/**
 * The price is `Price` in `unitRows.tsx` now, shared with the inspector's
 * saving-for row (U6): a bill and a holding are the same encoding, and two
 * copies of it are two places for a swatch to stop matching the board.
 */

/** On every tile that can be picked up, so a finger drags it instead of the page. */
const NO_TOUCH_PAN = { touchAction: "none" } as const;

/** The flyout's row of structures and tokens — wraps, now walls and power are in it (§31 S4–S6). */
const Row = styled("div")({ display: "flex", flexWrap: "wrap", gap: 8, maxWidth: 400 });

/** The category tile's caption: one word and the chevron, never wrapped. */
const Door = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  whiteSpace: "nowrap",
  color: theme.kit.color.inkDim
}));

/**
 * The kinds the icon set actually draws. Everything else keeps its glyph —
 * `STRUCTURE_GLYPH` is the table the strip and the dev menu already read, and
 * a name the registry does not hold is a crash rather than a fallback. The
 * kit's `moat` icon belongs to the Static Moat *card*, which is not a
 * structure kind and never reaches this bar.
 */
const STRUCTURE_ICON: Partial<Record<StructureName, IconName>> = {
  store: "store",
  turret: "turret"
};

/** A structure's picture: the icon where there is one, else its glyph. */
function StructureMark({ structure, glyph }: { structure: StructureName; glyph: string }) {
  const icon = STRUCTURE_ICON[structure];
  return <TileGlyph>{icon ? <Icon name={icon} size={24} /> : glyph}</TileGlyph>;
}

/** The price, said both ways: the tile's swatches and the sentence for them. */
const Bill = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.sm,
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  lineHeight: 1.4,
  color: theme.kit.color.ink
}));

/** What the thing does. One line, in the secondary ink — `HoverCard`'s body. */
const Role = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  lineHeight: 1.45,
  color: theme.kit.color.inkDim
}));

/** Why the tile is off, under the line saying what it would have been for. */
const Refusal = styled(Role)(({ theme }) => ({ color: theme.kit.color.rust }));

/**
 * A tile's card: what it costs, what it is for, and why it is off.
 *
 * **The swatches *and* the words** (designer ruling, U6 review). The tile
 * itself is 66px and carries numbers and colours only — the readouts' ruling —
 * so this is the one surface where the ladder gets named, and a card that
 * repeated the swatches without saying "iron" would leave the colour standing
 * for nothing. `Price` is passed no `arm` on purpose: with one it hangs a
 * `HoverCard` off every swatch, and a card inside a card is a card nobody can
 * reach.
 */
function TileCard({
  rows,
  caption,
  role,
  reason
}: {
  rows?: readonly PriceRow[];
  caption: ReactNode;
  role: string;
  reason?: string | null;
}) {
  return (
    <>
      <Bill>
        {rows !== undefined && <Price rows={rows} />}
        <span>{caption}</span>
      </Bill>
      <Role>{role}</Role>
      {reason ? <Refusal>{reason}</Refusal> : null}
    </>
  );
}

export function Hotbar({ view, onChoose, onLift, facingAngles }: HotbarProps) {
  const root = useChromeRect("hotbar", "left");
  const [open, setOpen] = useState(false);
  const carry = useTileCarry();
  const category = useRef<HTMLButtonElement | null>(null);
  // A state ref, not `useRef`: the dial's popover opens on the same render the
  // pawn tile is armed on, and `.current` is still null then — `HoverCard`
  // pays for this lesson too.
  const [pawn, setPawn] = useState<HTMLButtonElement | null>(null);
  const pawnTile = view.forge.find((tile) => tile.piece === "pawn");
  /** The facing dial is up: the pawn tile is armed and there is one to arm. */
  const facingOpen = pawnTile?.armed === true;

  const tokens = view.tokens.reduce((sum, one) => sum + one.count, 0);
  // The category tile stays lit while something inside it is armed, or the
  // flyout would close on the click that armed a store and leave nothing on
  // screen saying what the next board click means.
  const buildArmed = open || view.build.some((tile) => tile.armed) || view.tokens.some((tile) => tile.armed);
  // The dial speaks the board's numbers; the view names the direction.
  const { mintFacing } = view;
  const facingValue = mintFacing.facing === null ? null : DIR_INDEX[mintFacing.facing];
  const pickFacing = (facing: Direction | null) => {
    const name = facing === null ? null : DIR_NAME[facing];
    const found = mintFacing.options.find((one) => one.facing === name);
    if (found) onChoose(found.option);
  };

  return (
    <Column ref={root} data-testid="hotbar">
      <GroupTitle>Forge</GroupTitle>
      {view.forge.map((tile) => (
        // Every tile explains itself on the kit's own surface (designer
        // ruling, U6 review): the name, the price in words as well as
        // swatches, what the rank is *for*, and — when it is off — why. A
        // native `title` said the first two in the system font a second late,
        // and had nowhere to put the other two.
        <HoverCard
          key={tile.piece}
          title={tile.name}
          placement="right"
          // **Not while the dial is up.** Both hang off the same tile on the
          // same side, and the card sits a rung above every popover — so a
          // card over the dial is a label on an open drawer, and worse, the
          // pointer would have to cross it to reach the arrows.
          disabled={facingOpen && tile.piece === "pawn"}
          data-testid={`tile-card-forge-${PIECE_TYPE[tile.piece]}`}
          card={
            <TileCard
              rows={tile.rows}
              caption={tile.caption}
              role={tile.role}
              reason={tile.reason}
            />
          }
        >
          <Tile
            // Numeric, and these ids are API: they are the shelf's old ones, so
            // a smoke that clicked a recipe still clicks the same thing.
            data-testid={`forge-${PIECE_TYPE[tile.piece]}`}
            // Only the pawn's, and only because the facing dial hangs off it.
            ref={tile.piece === "pawn" ? setPawn : undefined}
            armed={tile.armed}
            aria-pressed={tile.armed}
            // An off tile's whole job is to say why, which is why `Tile` keeps
            // it hoverable rather than taking the native `disabled`.
            disabled={tile.reason !== null}
            // Or a touch drag off the tile is the browser's pan, and cancels.
            style={NO_TOUCH_PAN}
            // An off tile has nothing to pick up; its card already says why.
            onPointerDown={
              tile.reason === null ? carry.press((at) => onLift(tile.option, at)) : undefined
            }
            onClick={carry.click(() => onChoose(tile.option))}
          >
            <TileGlyph>
              <PieceMark piece={tile.piece} glyph={tile.glyph} size={24} />
            </TileGlyph>
            <Price rows={tile.rows} />
          </Tile>
        </HoverCard>
      ))}

      {/* **Which way new pawns face, asked where they are made** (designer
          ruling, U6 review). It opens with the pawn tile and closes when the
          tile is put down, so the question is asked exactly while it is live.

          Non-modal, and the board underneath stays live: the click that mints
          resolves on `pointerup` in `BoardCanvas`, and `ClickAwayListener`
          answers the document's `click` afterwards — so a click on a lit
          square mints and *then* puts the tool down, which closes this. The
          build flyout above coexists with the board the same way. */}
      <Popover
        open={facingOpen}
        anchorEl={pawn}
        // Escape and a click away both mean "put the tool down", which is the
        // one thing that can close this — `open` is derived from what is
        // armed, so a local `open` flag would be a second truth about it.
        onClose={() => {
          if (pawnTile) onChoose(pawnTile.option);
        }}
        placement="right-start"
        data-testid="mint-facing"
      >
        <SectionTitle>New pawns face</SectionTitle>
        <FacingDial value={facingValue} angles={facingAngles} onPick={pickFacing} />
      </Popover>

      <GroupTitle style={{ marginTop: 6 }}>Build</GroupTitle>
      {/* One door rather than four tiles: structures are the rarer act and a
          column that grew with the run's tokens would push the forge list off
          the screen. */}
      {/* The door says what is behind it — the one card with no price, since
          a door costs nothing and the tiles inside carry their own bills. */}
      <HoverCard
        title="Build"
        placement="right"
        // **Not while the flyout is up** — the kit's rule 3, the pawn tile's
        // exception for the pawn tile's reason. The flyout opens `right` off
        // this same tile and the card sits a rung above it, so a card here is a
        // label pasted over the drawer the click just opened, and the pointer
        // has to cross it to reach a warehouse.
        disabled={open}
        card="Warehouses, turrets and whatever your run has handed you. Standing one costs the turn."
        data-testid="tile-card-hotbar-build"
      >
        <Tile
          ref={category}
          data-testid="hotbar-build"
          armed={buildArmed}
          aria-expanded={open}
          aria-label="Structures and tokens"
          onClick={() => setOpen((was) => !was)}
        >
          <TileGlyph>▣</TileGlyph>
          {/* **"build", not the mock's "structures ▸".** Measured at 66px:
              "structures" plus the chevron is 71px against 53px of usable
              width, so it spilled out of the tile. The heading above says the
              same word, which is the cost of the door fitting — the card and
              the flyout are what name what is behind it. */}
          <Door>
            <TileCaption>build</TileCaption>
            <Icon name="chevronRight" size={10} />
          </Door>
          {tokens > 0 && <TileCount>×{tokens}</TileCount>}
        </Tile>
      </HoverCard>

      <Popover
        open={open}
        anchorEl={category.current}
        onClose={() => setOpen(false)}
        placement="right"
        data-testid="build-flyout"
      >
        <Row>
          {view.build.map((tile) => (
            // The same card as a forge tile, for the same reason: a swatch and
            // a number do not say "stone", and nothing else on the bar says
            // what a warehouse is.
            <HoverCard
              key={`build-${tile.building}`}
              title={tile.name}
              // **`bottom`, because the flyout is a row** (kit rule 3). The
              // column's own tiles open to the side; these have a neighbour
              // there, and `right` put every card on the tile beside it.
              placement="bottom"
              data-testid={`tile-card-build-${BUILDING_KIND[tile.building]}`}
              card={
                <TileCard
                  rows={tile.rows}
                  caption={tile.caption}
                  role={tile.role}
                  reason={tile.reason}
                />
              }
            >
              <Tile
                data-testid={`build-${BUILDING_KIND[tile.building]}`}
                armed={tile.armed}
                aria-pressed={tile.armed}
                disabled={tile.reason !== null}
                style={NO_TOUCH_PAN}
                // The flyout shuts as the click shuts it: the board is what
                // the drag is about, and the door stays lit while it is armed.
                onPointerDown={
                  tile.reason === null
                    ? carry.press((at) => {
                        onLift(tile.option, at);
                        setOpen(false);
                      })
                    : undefined
                }
                onClick={carry.click(() => {
                  onChoose(tile.option);
                  setOpen(false);
                })}
              >
                <StructureMark structure={tile.structure} glyph={tile.glyph} />
                <Price rows={tile.rows} />
              </Tile>
            </HoverCard>
          ))}
          {view.tokens.map((tile) => (
            // A token is held, never priced, so the count stands where the
            // bill would and the card carries the instruction as well as the
            // role — it is the one tile whose gesture has a second step.
            <HoverCard
              key={`token-${tile.structure}`}
              title={tile.name}
              placement="bottom"
              data-testid={`tile-card-token-${STRUCTURE_KIND[tile.structure]}`}
              card={
                <TileCard
                  caption={`${tile.count} in hand — click, then click bare ground you hold. Costs the turn.`}
                  role={tile.role}
                />
              }
            >
              <Tile
                data-testid={`token-${STRUCTURE_KIND[tile.structure]}`}
                armed={tile.armed}
                aria-pressed={tile.armed}
                style={NO_TOUCH_PAN}
                onPointerDown={carry.press((at) => {
                  onLift(tile.option, at);
                  setOpen(false);
                })}
                onClick={carry.click(() => {
                  onChoose(tile.option);
                  setOpen(false);
                })}
              >
                <StructureMark structure={tile.structure} glyph={tile.glyph} />
                <TileCaption>token</TileCaption>
                <TileCount>×{tile.count}</TileCount>
              </Tile>
            </HoverCard>
          ))}
        </Row>
      </Popover>
    </Column>
  );
}
